import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadSimple, CheckCircle, Warning, Spinner, Trash, Eye, ArrowsClockwise, PencilSimple } from "@phosphor-icons/react";
import { Section, errToast } from "@/components/tax/shared";
import { toast } from "sonner";

function FieldInput({ f, value, onChange, disabled, testId }) {
  if (f.type === "bool")
    return <input type="checkbox" data-testid={testId} checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-red-600" />;
  return (
    <input data-testid={testId} disabled={disabled} type={f.type === "text" ? "text" : "number"} step="0.01" value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      className="w-full h-9 rounded-md border border-input px-2.5 text-sm bg-white disabled:bg-slate-50" />
  );
}

function DocCard({ doc, schema, rid, locked, onChanged }) {
  const [fields, setFields] = useState(doc.fields);
  const [editing, setEditing] = useState(!doc.verified);
  const [busy, setBusy] = useState(false);
  const pending = doc.extraction_status === "pending";
  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onChanged(); } catch (e) { errToast(e); } finally { setBusy(false); }
  };
  const view = async () => {
    try {
      const res = await api.get(`/tax/returns/${rid}/documents/${doc.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch (e) { errToast(e); }
  };
  return (
    <div className="tax-card p-4" data-testid={`tax-doc-${doc.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs font-black text-red-600 bg-red-50 px-2 py-1 rounded">{doc.slip_type}</span>
        <span className="text-sm font-semibold text-slate-800 truncate max-w-[220px]">{doc.original_filename || "Entered manually"}</span>
        {pending && <span className="text-xs text-sky-700 flex items-center gap-1" data-testid="tax-doc-extracting"><Spinner className="h-3.5 w-3.5 animate-spin" /> Reading with AI…</span>}
        {doc.extraction_status === "failed" && <span className="text-xs text-amber-700 flex items-center gap-1"><Warning className="h-3.5 w-3.5" /> {doc.extraction_error}</span>}
        {doc.verified ? <span className="text-xs text-emerald-700 flex items-center gap-1" data-testid="tax-doc-verified"><CheckCircle className="h-4 w-4" weight="fill" /> Verified</span>
          : !pending && <span className="text-xs font-semibold text-orange-700" data-testid="tax-doc-unverified">Check these values</span>}
        {doc.detected_type && doc.detected_type !== doc.slip_type && <span className="text-xs text-amber-700">Looks like a {doc.detected_type}</span>}
        <div className="ml-auto flex gap-1">
          {doc.has_file && <Button size="sm" variant="ghost" data-testid={`tax-doc-view-${doc.id}`} onClick={view}><Eye className="h-4 w-4" /></Button>}
          {doc.has_file && !locked && <Button size="sm" variant="ghost" disabled={busy} data-testid={`tax-doc-reextract-${doc.id}`}
            onClick={() => call(() => api.post(`/tax/returns/${rid}/documents/${doc.id}/extract`), "Re-reading document…")}><ArrowsClockwise className="h-4 w-4" /></Button>}
          {!locked && <Button size="sm" variant="ghost" disabled={busy} data-testid={`tax-doc-delete-${doc.id}`}
            onClick={() => window.confirm("Delete this document permanently?") && call(() => api.delete(`/tax/returns/${rid}/documents/${doc.id}`), "Document deleted")}><Trash className="h-4 w-4 text-red-600" /></Button>}
        </div>
      </div>
      {!pending && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {schema.fields.map((f) => (
              <div key={f.key}>
                <p className="text-[11px] text-slate-500 mb-1">{f.label}</p>
                <FieldInput f={f} value={fields[f.key]} disabled={!editing || locked} testId={`tax-doc-field-${doc.id}-${f.key}`}
                  onChange={(v) => setFields({ ...fields, [f.key]: v })} />
              </div>
            ))}
          </div>
          {!locked && (
            <div className="flex justify-end gap-2 mt-3">
              {!editing ? <Button size="sm" variant="outline" data-testid={`tax-doc-edit-${doc.id}`} onClick={() => setEditing(true)}><PencilSimple className="h-4 w-4 mr-1" /> Correct values</Button>
                : <Button size="sm" disabled={busy} data-testid={`tax-doc-confirm-${doc.id}`} className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => call(() => api.put(`/tax/returns/${rid}/documents/${doc.id}`, { fields, verified: true }).then(() => setEditing(false)), "Values confirmed")}>
                  <CheckCircle className="h-4 w-4 mr-1" /> I've checked these values
                </Button>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Uploader({ rid, slipTypes, maxMb, onDone }) {
  const [type, setType] = useState("T4");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) return toast.error(`File is larger than ${maxMb} MB.`);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("slip_type", type);
    setBusy(true);
    try { await api.post(`/tax/returns/${rid}/documents`, fd); toast.success("Uploaded & encrypted. Reading values…"); onDone(); }
    catch (e) { errToast(e); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const manual = async () => {
    try { await api.post(`/tax/returns/${rid}/documents/manual`, { slip_type: type, fields: {}, verified: false }); onDone(); }
    catch (e) { errToast(e); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor="tax-upload-type" className="text-xs font-semibold text-slate-600 block mb-1.5">Document type</label>
        <select id="tax-upload-type" data-testid="tax-upload-type" value={type} onChange={(e) => setType(e.target.value)} className="h-10 rounded-md border border-input px-3 text-sm min-w-[260px]">
          {Object.entries(slipTypes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <input ref={input} type="file" accept="application/pdf,image/*" className="hidden" data-testid="tax-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
      <Button data-testid="tax-upload-btn" disabled={busy} onClick={() => input.current?.click()} className="bg-red-600 hover:bg-red-700 text-white rounded-full px-5">
        <UploadSimple className="h-4 w-4 mr-1.5" weight="bold" /> {busy ? "Encrypting & uploading…" : "Upload PDF or photo"}
      </Button>
      <Button variant="outline" className="rounded-full" data-testid="tax-manual-entry-btn" onClick={manual}>Enter manually</Button>
    </div>
  );
}

export function DocumentsStep({ ret, meta, locked, refresh }) {
  return (
    <div className="space-y-5" data-testid="tax-documents-step">
      <Section title="Tax documents" subtitle={`PDF, JPG, PNG or HEIC up to ${meta.settings.max_upload_mb} MB. Files are encrypted before storage. AI reads the boxes — always verify every value against your slip.`}>
        {locked ? <p className="text-sm text-slate-500">Documents are locked while your preparer has the return.</p>
          : <Uploader rid={ret.id} slipTypes={meta.slip_types} maxMb={meta.settings.max_upload_mb} onDone={refresh} />}
      </Section>
      {ret.documents.length === 0 && <p className="text-sm text-slate-500" data-testid="tax-docs-empty">No documents yet.</p>}
      {ret.documents.map((d) => (
        <DocCard key={`${d.id}-${d.extraction_status}-${d.verified_at}`} doc={d} schema={meta.slip_types[d.slip_type]} rid={ret.id} locked={locked} onChanged={refresh} />
      ))}
    </div>
  );
}
